import { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';

const EDITOR_LEVEL_ROLES = ['editor', 'senior_editor', 'admin', 'superuser', 'supervisor', 'producer'];
const ADMIN_ROLES = ['admin', 'superuser'];
const JOURNALIST_ROLES = ['journalist', 'social'];

interface UserRoleInfo {
  userId: string | null;
  role: string | null;
  fullName: string;
  email: string;
  loading: boolean;
  isEditor: boolean;
  isAdmin: boolean;
  isJournalist: boolean;
}

export function useUserRole(): UserRoleInfo {
  const [userId, setUserId] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || cancelled) {
          setLoading(false);
          return;
        }

        setUserId(user.id);
        setEmail(user.email || '');

        const { data: profile } = await supabase
          .from('profiles')
          .select('role, full_name')
          .eq('id', user.id)
          .single();

        if (!cancelled && profile) {
          setRole(profile.role || 'journalist');
          setFullName(profile.full_name || user.email || 'User');
        }
      } catch (err) {
        console.error('[useUserRole] Error loading profile:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);

  const isEditor = role !== null && EDITOR_LEVEL_ROLES.includes(role);
  const isAdmin = role !== null && ADMIN_ROLES.includes(role);
  const isJournalist = role !== null && JOURNALIST_ROLES.includes(role);

  return { userId, role, fullName, email, loading, isEditor, isAdmin, isJournalist };
}
